import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  FormHelperText,
  Grid,
  styled,
  TextField
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { Paragraph } from "app/components/Typography";
import { ErrorContext } from "app/contexts/ErrorContext";
import useCheckCompanyExists from "app/hooks/company/useCheckCompanyExists";
import { useGenerateOTP } from "app/hooks/otp/useGenerateOTP";
import { useVerifyOTP } from "app/hooks/otp/useVerifyOTP";
import useAuth from "app/hooks/useAuth";
import { AppRoutes, PhoneRegex } from "app/utils/global_constants";
import { Industries, statesOfIndia } from "app/utils/selection_constants";
import { Formik } from "formik";
import { useContext, useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import posting_photo from "../../../assets/illustrations/posting_photo.svg";
import PasswordTextField from "./shared/PasswordTextField";
import PrivacyPolicyDialog from "./shared/PrivacyPolicyDialog";
import TCDialog from "./shared/TCDialog";
import VerifyOTPDialog from "./shared/VerifyOTPDialog";
// STYLED COMPONENTS
const FlexBox = styled(Box)(() => ({
  display: "flex",
  alignItems: "center"
}));

const JustifyBox = styled(FlexBox)(() => ({
  justifyContent: "center"
}));

const ContentBox = styled(JustifyBox)(() => ({
  height: "100%",
  padding: "32px",
  background: "rgba(0, 0, 0, 0.01)"
}));

const DialogButton = styled(Button)(() => ({ fontSize: "12px", fontWeight: "bold" }));

const JWTRegister = styled(JustifyBox)(() => ({
  background: "#1A2038",
  minHeight: "100vh !important",
  "& .card": {
    maxWidth: 800,
    minHeight: 400,
    margin: "1rem",
    display: "flex",
    borderRadius: 12,
    alignItems: "center"
  }
}));

// initial login credentials
const initialValues = {
  jp_email: "",
  jp_contact_no: "",
  jp_password: "",
  jp_company_name: "",
  jp_cin: "",
  jp_industry: null,
  remember: false
};

// form field validation schema
const validationSchema = Yup.object().shape({
  jp_industry: Yup.object().nullable().required("Industry is required"),
  jp_contact_no: Yup.string()
    .required("Contact number is required")
    .matches(PhoneRegex, "Enter a valid contact number"),
  jp_password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be 8 character length")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
      "Password must include an uppercase letter, a lowercase letter, a number, and a special character."
    )
    .required("Password is required"),
  jp_cin: Yup.string()
    .notRequired()
    .matches(/^[A-Z0-9]{21}$/, {
      message: "Enter a valid C.I.N number",
      excludeEmptyString: true
    }),
  remember: Yup.boolean()
    .oneOf([true], "Please accept Terms and Conditions before proceeding")
    .required("Please accept Terms and Conditions before proceeding"),
  jp_email: Yup.string()
    .required("Email is required")
    .matches(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      "Invalid Email"
    )
    .email("Invalid Email address"),
  jp_company_name: Yup.string().required("Company name is required")
});

export default function JwtRegisterCompany() {
  const location = useLocation();
  const { register, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { success, error, message, severity, setErrorState, setSuccessState, clearErrorState } =
    useContext(ErrorContext);

  const [otp, setOTP] = useState();
  const [otpError, setOTPError] = useState(false);
  const [registerValues, setRegisterValues] = useState();
  const [loading, setLoading] = useState(false);

  // OTP Timer State
  const [otpTimer, setOtpTimer] = useState(0); // seconds left
  const OTP_RESEND_DURATION = 30; // 1 minute constant
  const timerRef = useRef();
  // constrols dialog that displays the terms and conditions
  const [showTC, setShowTC] = useState(false);
  // controls dialog that displays the privacy policy
  const [showPP, setShowPP] = useState(false);
  // controls otp dialog
  const [showVerifyOTP, setShowVerifyOTP] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Register Company
  // Helper to start timer
  const startOtpTimer = () => {
    setOtpTimer(OTP_RESEND_DURATION);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const registerCompany = async (values) => {
    setOTPError(false);
    setLoading(true);
    try {
      const registerState = await register(
        { ...values, jp_industry: values.jp_industry.label },
        "company"
      );
      if (registerState && registerState.error) {
        setErrorState(true, registerState.message, "error");
      }
      if (registerState && registerState.error === false) {
        await refreshUser();
        setRegistrationSuccess(true);
        setSuccessState(true, "Registration successfull. Redirecting.....");
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
    } finally {
      setShowVerifyOTP(false);
    }
  };

  /* OTP Generation */
  const onOTPGenerated = () => {
    setShowVerifyOTP(true);
    startOtpTimer();
    setSuccessState(true, "OTP sent to your email. Please verify.", "success");
  };

  const onOTPGeneratedError = (e) => {
    if (e?.status === 429) {
      setErrorState(
        true,
        "You have requested OTP too many times. Please try again later.",
        "error"
      );
    } else {
      setErrorState(true, "Failed to generate OTP. Please try again.", "error");
    }
  };

  const { mutate: generatedOTP, isPending: isGeneratingOTP } = useGenerateOTP(
    onOTPGenerated,
    onOTPGeneratedError
  );

  const onCompanyCheckSuccess = (data, _) => {
    if (data) {
      setErrorState(true, "Company already exists", "error");
    } else {
      generatedOTP({
        email: registerValues.jp_email,
        type: "verify-email",
        name: registerValues.jp_company_name
      });
    }
  };

  const onCompanyCheckFail = () => {
    setErrorState(true, "Cannot register at the moment", "error");
  };

  const { mutate: checkCompanyExists } = useCheckCompanyExists({
    onSuccess: onCompanyCheckSuccess,
    onError: onCompanyCheckFail
  });

  const { mutate: verifyOTP } = useVerifyOTP({
    onSuccess: () => registerCompany(registerValues),
    onError: () => setOTPError(true)
  });

  const handleFormSubmit = (values) => {
    setRegisterValues(values);
    checkCompanyExists({ email: values.jp_email });
  };

  const loginLink = location.pathname.includes("company")
    ? "/session/login/company"
    : "/session/login/candidate";

  return (
    <JWTRegister>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={error || success}
        onClose={() => {
          const redirectToCheckout = searchParams.get("redirectToCheckout");
          const plan = searchParams.get("plan");
          if (registrationSuccess && !redirectToCheckout) {
            navigate(AppRoutes.ROUTE_DASHBOARD);
          } else if (registrationSuccess && redirectToCheckout && plan) {
            navigate(`${AppRoutes.ROUTE_CHECKOUT}?plan=${plan}&type=employer`);
          }
          clearErrorState();
        }}
        autoHideDuration={3000}
        color={severity}
      >
        <Alert severity={severity}>{message}</Alert>
      </Snackbar>
      {/** Terms and Condition */}
      <TCDialog open={showTC} handleClose={() => setShowTC(false)} />
      {/** Privacy Policy*/}
      <PrivacyPolicyDialog open={showPP} handleClose={() => setShowPP(false)} />
      {/** Verify OTP */}
      <VerifyOTPDialog
        open={showVerifyOTP}
        otpError={otpError}
        verifyState={loading}
        resendState={isGeneratingOTP}
        otpTimer={otpTimer}
        otpDuration={OTP_RESEND_DURATION}
        handleOTPChange={(otp) => setOTP(otp)}
        handleVerifyOTP={() => verifyOTP({ email: registerValues.jp_email, otp: otp })}
        handleResendOTP={() => {
          // startOtpTimer();
          generatedOTP({
            email: registerValues.jp_email,
            type: "verify-email",
            name: registerValues.jp_company_name
          });
        }}
        handleClose={() => setShowVerifyOTP(false)}
      />
      <Card className="card">
        <Grid container>
          <Grid item sm={6} xs={12}>
            <ContentBox>
              <img width="100%" alt="Register" src={posting_photo} />
            </ContentBox>
          </Grid>

          <Grid item sm={6} xs={12}>
            <Box p={4} height="100%">
              <Formik
                onSubmit={handleFormSubmit}
                initialValues={initialValues}
                validationSchema={validationSchema}
              >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
                  <form onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      size="small"
                      type="text"
                      name="jp_company_name"
                      label="Company name"
                      variant="outlined"
                      onBlur={handleBlur}
                      value={values.jp_company_name}
                      onChange={handleChange}
                      helperText={touched.jp_company_name && errors.jp_company_name}
                      error={Boolean(errors.jp_company_name && touched.jp_company_name)}
                      sx={{ mb: 3 }}
                    />

                    <TextField
                      fullWidth
                      size="small"
                      type="email"
                      name="jp_email"
                      label="Email"
                      variant="outlined"
                      autoComplete="email"
                      onBlur={handleBlur}
                      value={values.jp_email}
                      onChange={handleChange}
                      helperText={touched.jp_email && errors.jp_email}
                      error={Boolean(errors.jp_email && touched.jp_email)}
                      sx={{ mb: 3 }}
                    />

                    <TextField
                      fullWidth
                      size="small"
                      type="text"
                      name="jp_contact_no"
                      value={values.jp_contact_no}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Contact No."
                      variant="outlined"
                      error={Boolean(errors.jp_contact_no && touched.jp_contact_no)}
                      helperText={touched.jp_contact_no && errors.jp_contact_no}
                      inputProps={{ maxLength: 25 }}
                      sx={{ mb: 3 }}
                    />

                    <Autocomplete
                      fullWidth
                      size="small"
                      options={Industries}
                      getOptionLabel={(option) => option.label || ""}
                      value={values.jp_industry}
                      onChange={(_, value) => {
                        // Formik setFieldValue
                        handleChange({ target: { name: "jp_industry", value } });
                      }}
                      onBlur={handleBlur}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="jp_industry"
                          label="Industry"
                          variant="outlined"
                          error={Boolean(errors.jp_industry && touched.jp_industry)}
                          helperText={touched.jp_industry && errors.jp_industry}
                          sx={{ mb: 3 }}
                        />
                      )}
                    />

                    <TextField
                      fullWidth
                      size="small"
                      type="text"
                      name="jp_cin"
                      label="CIN (Corporate Identification Number)"
                      variant="outlined"
                      onBlur={handleBlur}
                      value={values.jp_cin}
                      onChange={handleChange}
                      helperText={touched.jp_cin && errors.jp_cin}
                      error={Boolean(errors.jp_cin && touched.jp_cin)}
                      sx={{ mb: 3 }}
                    />

                    <PasswordTextField
                      name={"jp_password"}
                      value={values.jp_password}
                      onChange={handleChange}
                      helperText={touched.jp_password && errors.jp_password}
                      errors={Boolean(errors.jp_password && touched.jp_password)}
                      onBlur={handleBlur}
                    />

                    <FlexBox gap={1} alignItems="center">
                      <Checkbox
                        size="small"
                        name="remember"
                        onChange={handleChange}
                        checked={values.remember}
                        sx={{ padding: 0 }}
                      />

                      <Paragraph fontSize={13}>
                        I have read and agree to the terms of service.
                      </Paragraph>
                    </FlexBox>
                    {errors.remember && (
                      <FormHelperText sx={{ color: "red" }}>{errors.remember}</FormHelperText>
                    )}

                    <Box display={"flex"} gap={2} mt={2}>
                      <DialogButton
                        size="small"
                        fullWidth
                        sx={{ flex: 1 }}
                        variant="outlined"
                        onClick={() => setShowTC(true)}
                      >
                        View Terms & Conditions
                      </DialogButton>
                      <DialogButton size="small" variant="outlined" onClick={() => setShowPP(true)}>
                        View Privacy Policy
                      </DialogButton>
                    </Box>

                    <LoadingButton
                      type="submit"
                      color="primary"
                      fullWidth
                      loading={isGeneratingOTP && !showVerifyOTP}
                      variant="contained"
                      sx={{ mb: 2, mt: 2 }}
                    >
                      Register
                    </LoadingButton>

                    <Paragraph sx={{ textAlign: "center" }}>
                      Already have an account?
                      <NavLink to={loginLink} style={{ color: "#1976D2", marginLeft: 5 }}>
                        Login
                      </NavLink>
                    </Paragraph>
                  </form>
                )}
              </Formik>
            </Box>
          </Grid>
        </Grid>
      </Card>
    </JWTRegister>
  );
}
